import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

@Injectable({
    providedIn: 'root',
})
export class SEOService {
    /**
     * Canonical link tag
     */
    private canonicalLink: HTMLLinkElement = null;

    public constructor(
        @Inject(PLATFORM_ID) protected platformId: any,
        @Inject(DOCUMENT) private doc,
        private meta: Meta,
        private router: Router,
    ) {
        this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe(() => {
            // reset meta description to empty value
            // (real content should be set by Features in ngOnInit of route Component)
            this.setMetaTag('description', '');
            // reset canonical tag
            this.resetCanonicalTag();
        });
    }

    public setMetaTag(name: string, content: string): void {
        this.meta.updateTag({ name, content });
    }

    public removeMetaTag(name: string): void {
        this.meta.removeTag(`name="${name}"`);
    }

    /**
     * @deprecated Use the new {@link setMetaTag} method instead.
     * Sets meta description tag
     * @param {string} content
     */
    public setMetaDescription(content: string): void {
        this.setMetaTag('description', content);
    }

    /**
     * Sets canonical link tag (`<link rel=canonical>`) with provided route
     * This method must be used with route alias defined in ecm
     * example : on `/compte/rdv-boutique` route, use canonical route `rdv-boutique` (without Feature root path)
     *           so crawlers will only index `rdv-boutique` path (and not `/compte/rdv-boutique` path)
     * @param {string} route Canonical Route
     */
    public setCanonicalRoute(route: string): void {
        if (isPlatformBrowser(this.platformId)) {
            if (!this.canonicalLink) {
                this.canonicalLink = this.doc.createElement('link');
                this.canonicalLink.setAttribute('rel', 'canonical');
                this.doc.head.appendChild(this.canonicalLink);
            }
            const origin: string = window.location.origin;
            const cleanRoute: string = route.replace(/^\/|\/$/g, ''); // escape leading/trailing slashes in route
            const canonicalUrl: string = origin + '/' + cleanRoute;
            this.canonicalLink.setAttribute('href', canonicalUrl);
        }
    }

    /**
     * Resets canonical link tag by removing `href` attribute
     */
    private resetCanonicalTag(): void {
        if (this.canonicalLink) {
            this.canonicalLink.removeAttribute('href');
        }
    }
}
